import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../auth/context/AuthContext';
import { planFeatureForPath } from './planFeatures';
import { getWorkspaceBilling } from './services/billingService';
import type { WorkspaceBilling } from './types/billing';

type PlanEntitlementsState = {
  ready: boolean;
  unrestricted: boolean;
  plansHidden: boolean;
  canManagePlans: boolean;
  features: ReadonlySet<string>;
  hasFeature: (feature: string) => boolean;
  noteWorkspace: (workspace: WorkspaceBilling) => void;
};

const allowEverything: PlanEntitlementsState = {
  ready: true,
  unrestricted: true,
  plansHidden: false,
  canManagePlans: false,
  features: new Set<string>(),
  hasFeature: () => true,
  noteWorkspace: () => undefined,
};

const PlanEntitlementsContext = createContext<PlanEntitlementsState>(allowEverything);

const toFeatureSet = (workspace: WorkspaceBilling) =>
  new Set(workspace.entitlements.features.map(feature => feature.trim()).filter(Boolean));

export const PlanEntitlementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [ready, setReady] = useState(false);
  const [unrestricted, setUnrestricted] = useState(false);
  const [plansHidden, setPlansHidden] = useState(false);
  const [canManagePlans, setCanManagePlans] = useState(false);
  const [features, setFeatures] = useState<ReadonlySet<string>>(() => new Set());

  const noteWorkspace = useCallback((workspace: WorkspaceBilling) => {
    setPlansHidden(workspace.plansHidden);
    setCanManagePlans(workspace.canEditFeatures);
    // A platform operator keeps the whole workspace open so they can support it after a
    // trial ends. The plan lock still applies to everyone else in that workspace.
    if (!workspace.entitlementsKnown || workspace.canEditFeatures) {
      setUnrestricted(true);
      setFeatures(new Set());
      setReady(true);
      return;
    }

    setUnrestricted(false);
    setFeatures(toFeatureSet(workspace));
    setReady(true);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setReady(true);
      setUnrestricted(false);
      setPlansHidden(false);
      setCanManagePlans(false);
      setFeatures(new Set());
      return;
    }

    let cancelled = false;
    setReady(false);
    getWorkspaceBilling()
      .then(workspace => {
        if (!cancelled) {
          noteWorkspace(workspace);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUnrestricted(true);
          setPlansHidden(false);
          setCanManagePlans(false);
          setFeatures(new Set());
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, noteWorkspace]);

  const hasFeature = useCallback(
    (feature: string) => unrestricted || features.has(feature),
    [features, unrestricted],
  );

  const value = useMemo(
    () => ({ ready, unrestricted, plansHidden, canManagePlans, features, hasFeature, noteWorkspace }),
    [canManagePlans, features, hasFeature, noteWorkspace, plansHidden, ready, unrestricted],
  );

  return (
    <PlanEntitlementsContext.Provider value={value}>
      {children}
    </PlanEntitlementsContext.Provider>
  );
};

export const usePlanEntitlements = () => useContext(PlanEntitlementsContext);

export const PlansPageGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { ready, plansHidden, canManagePlans } = usePlanEntitlements();
  if (!ready) {
    return null;
  }

  if (plansHidden && !canManagePlans) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export const PlanFeatureRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { ready, hasFeature } = usePlanEntitlements();
  const feature = planFeatureForPath(location.pathname);

  if (!feature) {
    return <>{children}</>;
  }

  if (!ready) {
    return null;
  }

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  return <PlanUpgradeState feature={feature} />;
};

const PlanUpgradeState: React.FC<{ feature: string }> = ({ feature }) => {
  const { t } = useTranslation('billing');
  const label = t(`features.${feature}`, { defaultValue: feature });

  return (
    <div className="company-section" style={{ marginTop: 24 }}>
      <h3>{t('upgrade.title')}</h3>
      <p>{t('upgrade.help', { feature: label })}</p>
      <Link to="/plans">{t('upgrade.action')}</Link>
    </div>
  );
};
